import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Search,
    Menu,
    LogOut,
    Shield,
    UserCheck,
    Briefcase,
    Users as UsersIcon,
    Headset,
    Stethoscope,
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../hooks/useAppStore';
import { selectUserRole, logout } from '../store/slices/authSlice';
import { getNavigationForRole, canRequestInvestigation } from '../config/navigation';
import { cn } from '../utils/cn';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import Logo3 from '../assets/newlogo.svg';
import type { NavItem } from '../types';
import { UserRole } from '../types';
import { SidebarExplorer } from '@podus/ui';
import type { Product } from '@podus/ui';

interface YourCheckaSidebarProps {
    activeItem?: string;
    className?: string;
    onRequestInvestigation?: () => void;
    onSupport?: () => void;
    userRole?: UserRole;
}

interface SidebarContentProps {
    activeItem?: string | undefined;
    onRequestInvestigation?: (() => void) | undefined;
    onSupport?: (() => void) | undefined;
    navItems: NavItem[];
    showRequestButton: boolean;
    userRole?: UserRole | undefined;
    // Called after any nav/CTA inside the sidebar triggers — the mobile sheet
    // uses this to auto-close so the user lands on the page, not the menu.
    onNavigate?: () => void;
}

function SidebarContent({
    activeItem = 'dashboard',
    onRequestInvestigation,
    onSupport,
    navItems,
    showRequestButton,
    userRole,
    onNavigate,
}: SidebarContentProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        onNavigate?.();
    };

    const handleRequestInvestigation = () => {
        onRequestInvestigation?.();
        onNavigate?.();
    };

    const isActive = (path: string) => {
        if (activeItem) {
            return navItems.find((item) => item.id === activeItem)?.path === path;
        }
        return location.pathname === path;
    };

    // Get role display info
    const getRoleDisplay = () => {
        switch (userRole) {
            case UserRole.SUPER_ADMIN:
                return { label: 'Super Admin', icon: Shield, color: 'bg-purple-100 text-purple-700 border-purple-200' };
            case UserRole.AGENT:
                return { label: 'Agent', icon: UserCheck, color: 'bg-blue-100 text-blue-700 border-blue-200' };
            case UserRole.EMPLOYER:
                return { label: 'Employer', icon: Briefcase, color: 'bg-green-100 text-green-700 border-green-200' };
            case UserRole.HR:
                return {
                    label: 'HR Partner',
                    icon: UsersIcon,
                    color: 'bg-orange-100 text-orange-700 border-orange-200',
                };
            case UserRole.CUSTOMER_SUCCESS:
                return {
                    label: 'Customer Success',
                    icon: Headset,
                    color: 'bg-teal-100 text-teal-700 border-teal-200',
                };
            case UserRole.MEDICAL:
                return {
                    label: 'Medical',
                    icon: Stethoscope,
                    color: 'bg-rose-100 text-rose-700 border-rose-200',
                };
            case UserRole.NOTARY:
                return {
                    label: 'Notary',
                    icon: Shield,
                    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
                };
            case UserRole.POLICE:
                return {
                    label: 'Police',
                    icon: Shield,
                    color: 'bg-slate-100 text-slate-700 border-slate-200',
                };
            default:
                return { label: 'User', icon: UserCheck, color: 'bg-gray-100 text-gray-700 border-gray-200' };
        }
    };

    const roleDisplay = getRoleDisplay();
    // const RoleIcon = roleDisplay.icon

    return (
        <div className="flex h-full flex-col overflow-y-auto bg-[#EDEFF3] py-8 px-4">
            {/* Logo */}
            <div className="mb-6 px-2">
                <img src={Logo3} alt="YourChecka Logo" className="h-9 w-auto" />
            </div>

            {/* User Role Badge */}
            <div className="mb-6 px-2">
                <Badge
                    variant="outline"
                    className={cn('w-full justify-center py-2 font-medium text-xs', roleDisplay.color)}
                >
                    {/* <RoleIcon className='w-3.5 h-3.5 mr-1.5' /> */}
                    {roleDisplay.label}
                </Badge>
            </div>

            {/* Request Investigation Button - Hidden for Super Admin, Agent, HR */}
            {showRequestButton &&
                userRole !== UserRole.SUPER_ADMIN &&
                userRole !== UserRole.AGENT &&
                userRole !== UserRole.HR && (
                    <div className="mb-6">
                        <button
                            onClick={handleRequestInvestigation}
                            className="w-full bg-[#1a1d29] hover:bg-gray-800 text-white rounded-[20px] px-5 py-3.5 flex items-center justify-between transition-colors group shadow-sm"
                        >
                            <div className="flex items-center gap-3">
                                <Search className="w-[18px] h-[18px] text-[#FF6B35]" />
                                <span className="font-medium text-[15px]">
                                    {userRole?.toLowerCase() === 'agent' ? 'Submit Report' : 'Request Investigation'}
                                </span>
                            </div>
                            <svg
                                className="w-4 h-4 group-hover:translate-x-0.5 transition-transform flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                )}

            {/* Navigation */}
            <nav className="flex-1 space-y-1">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    if (!Icon) return null;

                    // Handle Support item specially based on role
                    const handleClick = () => {
                        if (item.id === 'support' && onSupport) {
                            onSupport();
                        } else {
                            navigate(item.path);
                        }
                        onNavigate?.();
                    };

                    return (
                        <button
                            key={item.id}
                            onClick={handleClick}
                            className={cn(
                                'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-left',
                                active
                                    ? 'bg-white text-gray-900 font-medium shadow-sm'
                                    : 'text-gray-600 hover:bg-white/50',
                            )}
                        >
                            <Icon className={cn('w-[18px] h-[18px]', active ? 'text-[#FF6B35]' : 'text-gray-500')} />
                            <span className="text-[15px]">{item.label}</span>
                        </button>
                    );
                })}
            </nav>

            {/* Explore Other Products */}
            <div className="mt-4 pt-4 border-t border-gray-200 px-2">
                <SidebarExplorer
                    currentApp="podus-checker"
                    onNavigate={(product: Product) => {
                        onNavigate?.();
                        const token = localStorage.getItem('access_token');
                        const url = new URL(product.href);
                        if (token) url.searchParams.set('accessToken', token);
                        window.location.href = url.toString();
                    }}
                />
            </div>

            {/* Logout Button */}
            <div className="mt-2 pt-4 border-t border-gray-200">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-left text-gray-600 hover:bg-white/50 hover:text-red-600"
                >
                    <LogOut className="w-[18px] h-[18px]" />
                    <span className="text-[15px]">Logout</span>
                </button>
            </div>
        </div>
    );
}

export function YourCheckaSidebar({
    activeItem,
    className,
    onRequestInvestigation,
    onSupport,
    userRole: userRoleProp,
}: YourCheckaSidebarProps) {
    const userRoleFromStore = useAppSelector(selectUserRole);
    const userRole = userRoleProp || userRoleFromStore;
    const navItems = getNavigationForRole(userRole);
    const showRequestButton = canRequestInvestigation(userRole);
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className={cn('hidden lg:block h-screen w-[300px] fixed left-0 top-0', className)}>
                <SidebarContent
                    activeItem={activeItem}
                    onRequestInvestigation={onRequestInvestigation}
                    onSupport={onSupport}
                    navItems={navItems}
                    showRequestButton={showRequestButton}
                    userRole={userRole}
                />
            </aside>

            {/* Mobile Sidebar */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="fixed left-4 top-4 z-50 lg:hidden bg-white shadow-md"
                    >
                        <Menu className="h-6 w-6" />
                        <span className="sr-only">Toggle menu</span>
                    </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] p-0">
                    <SidebarContent
                        activeItem={activeItem}
                        onRequestInvestigation={onRequestInvestigation}
                        onSupport={onSupport}
                        navItems={navItems}
                        showRequestButton={showRequestButton}
                        userRole={userRole}
                        onNavigate={() => setMobileOpen(false)}
                    />
                </SheetContent>
            </Sheet>
        </>
    );
}
